/**
 * "Switch On" split login — exact port of the approved standalone design.
 * Left: warm dark brand panel (Cormorant Garamond). Right: cream form panel.
 * Shared .lm-* classes style the EmailStep / OtpStep children.
 */

const Bulb = ({ size, strokeWidth, style }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none"
       stroke="#b68235" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
       style={style}>
    <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
    <path d="M9 18h6" />
    <path d="M10 22h4" />
  </svg>
);

export default function SwitchOnSplit({ children }) {
  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:[grid-template-columns:5fr_6fr]"
         style={{ background: '#f3f2f2', color: '#201f1d', fontFamily: "'Lora', system-ui, sans-serif" }}
         data-testid="login-variant-6">

      {/* Brand panel */}
      <div className="hidden lg:flex relative overflow-hidden flex-col justify-between"
           style={{ background: 'oklch(0.24 0.012 75)', color: '#f8f4f4', padding: '56px 64px' }}>
        <Bulb size={520} strokeWidth={0.55}
              style={{ position: 'absolute', right: -120, bottom: -100, opacity: 0.16 }} />
        <div className="flex items-center" style={{ gap: 12 }}>
          <Bulb size={22} strokeWidth={1.5} />
          <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, letterSpacing: '0.04em' }}>Lumyn</span>
        </div>
        <div className="relative" style={{ maxWidth: '26ch' }}>
          <div style={{ width: 44, height: 1, background: '#b68235', marginBottom: 28 }} />
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, fontSize: 64,
                       lineHeight: 1.08, margin: '0 0 20px -0.042em', letterSpacing: '-0.01em' }}>
            Switch on learning.
          </h1>
          <p style={{ fontSize: 17, lineHeight: 1.65, margin: 0, color: '#d7d3d3', textWrap: 'pretty' }}>
            One light for trainers, learners and everyone keeping score.
          </p>
        </div>
        <div style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#bab6b6' }}>
          Lumyn · Powered by Zefficacy
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center" style={{ padding: '56px 40px' }}>
        <div style={{ width: 380 }}>
          {/* Compact brand row for mobile (brand panel hidden) */}
          <div className="flex lg:hidden items-center mb-10" style={{ gap: 10 }}>
            <Bulb size={22} strokeWidth={1.5} />
            <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, letterSpacing: '0.04em' }}>Lumyn</span>
          </div>
          {children}
        </div>
      </div>

      <style>{`
        .lm-kicker { font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: #7d5411; margin-bottom: 14px; }
        .lm-h2 { font-family: 'Cormorant Garamond', serif; font-weight: 500; font-size: 40px; line-height: 1.1; margin: 0 0 12px -0.042em; color: #201f1d; }
        .lm-body { font-size: 15px; line-height: 1.65; margin: 0 0 32px; color: #7d7979; }
        .lm-field { margin-bottom: 24px; }
        .lm-field > label { display: block; font-size: 12px; margin-bottom: 5px; color: rgba(32,31,29,0.7); }
        .lm-input { width: 100%; min-height: 36px; padding: 6px 10px; font-family: inherit; font-size: 14px;
                    color: #201f1d; caret-color: #b68235; background: transparent;
                    border: 1px solid rgba(32,31,29,0.16); border-radius: 4px; outline: none;
                    transition: border-color 0.15s ease; }
        .lm-input::placeholder { color: #bab6b6; }
        .lm-input:hover { border-color: rgba(32,31,29,0.45); }
        .lm-input:focus-visible { border-color: #b68235; }
        .lm-btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; cursor: pointer;
                  width: 100%; margin-top: 8px; font-family: 'Cormorant Garamond', serif; font-weight: 600;
                  font-size: 14px; line-height: 1.2; color: #b68235; background: transparent;
                  border: 1px solid #b68235; padding: 9px 17px; border-radius: 4px;
                  transition: background-color 0.15s ease; }
        .lm-btn:hover { background: rgba(182,130,53,0.12); }
        .lm-btn:active { background: rgba(182,130,53,0.22); }
        .lm-btn:disabled { opacity: 0.45; cursor: not-allowed; }
        .lm-divider { height: 1px; background: rgba(32,31,29,0.16); margin: 32px 0 20px; }
        .lm-foot { font-size: 13px; line-height: 1.6; margin: 0; color: #9b9797; }
        .lm-link { color: #7d5411; background: none; border: none; padding: 0; cursor: pointer;
                   font: inherit; text-decoration: underline; text-decoration-thickness: 1px; text-underline-offset: 3px; }
        .lm-link:disabled { color: #bab6b6; cursor: default; text-decoration: none; }
        .lm-error { display: flex; align-items: center; gap: 6px; font-size: 13px; color: #b3413a; margin: -12px 0 20px; }
        .lm-check { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #9b9797;
                    user-select: none; margin: -8px 0 20px; cursor: pointer; }
        .lm-check input { width: 14px; height: 14px; accent-color: #b68235; }
      `}</style>
    </div>
  );
}
